import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    ControlContainer,
    FormControl,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { Subject, takeUntil } from 'rxjs';
import { SectionHeaderComponent } from '../../../../shared/components/section-header/section-header.component';
import { DomainHostingControls, DomainType } from '../../../../shared/models/domain.model';
import { FORM_SECTION_DESCRIPTIONS } from '../../const/form-help-text.const';
import { AppForm } from '../../models/app-form';
import { customDomainValidator, subdomainValidator } from '../../validators/validators';

@Component({
    selector: 'app-domain-hosting-section',
    imports: [
        ReactiveFormsModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatButtonModule,
        MatIconModule,
        SectionHeaderComponent,
    ],
    templateUrl: './domain-hosting-section.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class DomainHostingSectionComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void> = new Subject<void>();

    public readonly CARGOSPRINT_SUFFIX = '.cargosprint.com';
    public readonly sectionDescription = FORM_SECTION_DESCRIPTIONS.domainHosting;

    constructor(private parent: FormGroupDirective) {}

    get parentForm(): FormGroup<AppForm> {
        return this.parent?.form;
    }

    get domainHostingGroup(): FormGroup<DomainHostingControls> {
        return this.parentForm?.controls?.domainHosting as FormGroup<DomainHostingControls>;
    }

    get domainTypeControl(): FormControl<DomainType | null> {
        return this.domainHostingGroup?.controls?.domainType;
    }

    get domainValueControl(): FormControl<string | null> {
        return this.domainHostingGroup?.controls?.domainValue;
    }

    get isSubdomain(): boolean {
        return this.domainTypeControl?.value === 'subdomain';
    }

    get isCustom(): boolean {
        return this.domainTypeControl?.value === 'custom';
    }

    get resolvedDomain(): string {
        const val = this.domainValueControl?.value?.trim() ?? '';
        if (this.isSubdomain && val) {
            return `${val}${this.CARGOSPRINT_SUFFIX}`;
        }
        return val;
    }

    get subdomainFormatError(): string | null {
        return this.domainValueControl?.getError('subdomainFormat') ?? null;
    }

    get subdomainReservedError(): string | null {
        return this.domainValueControl?.getError('subdomainReserved') ?? null;
    }

    get customDomainProtocolError(): string | null {
        return this.domainValueControl?.getError('customDomainProtocol') ?? null;
    }

    get customDomainPathError(): string | null {
        return this.domainValueControl?.getError('customDomainPath') ?? null;
    }

    get customDomainNoDotError(): string | null {
        return this.domainValueControl?.getError('customDomainNoDot') ?? null;
    }

    get customDomainLengthError(): string | null {
        return this.domainValueControl?.getError('customDomainLength') ?? null;
    }

    get customDomainLabelError(): string | null {
        return this.domainValueControl?.getError('customDomainLabel') ?? null;
    }

    get isRequiredError(): boolean {
        return (this.domainValueControl?.hasError('required') && this.domainValueControl?.touched) ?? false;
    }

    ngOnInit(): void {
        this.subscribeToTypeChanges();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private subscribeToTypeChanges(): void {
        this.domainTypeControl?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((type) => {
            this.domainValueControl?.reset('');
            this.applyValidators(type);
        });

        this.applyValidators(this.domainTypeControl?.value);
    }

    private applyValidators(type: DomainType | null | undefined): void {
        if (!this.domainValueControl) return;

        this.domainValueControl.clearValidators();

        if (type === 'subdomain') {
            this.domainValueControl.setValidators([Validators.required, subdomainValidator()]);
        } else if (type === 'custom') {
            this.domainValueControl.setValidators([Validators.required, customDomainValidator()]);
        }

        this.domainValueControl.updateValueAndValidity();
    }

    public patchDomainValues(type: DomainType, value: string): void {
        const patchValue =
            type === 'subdomain' && value.endsWith(this.CARGOSPRINT_SUFFIX)
                ? value.replace(this.CARGOSPRINT_SUFFIX, '')
                : value;

        this.domainHostingGroup?.patchValue({ domainType: type, domainValue: patchValue });
        this.applyValidators(type);
    }
}
